/**
 * Protocol layer: owns the handshake, the clock, the input stream and the
 * per-entity snapshot timelines.
 *
 * Knows nothing about rendering, and reaches the network only through the
 * `Transport` interface.
 */

import {
  ByteWriter,
  CloseCode,
  INPUT_REDUNDANCY,
  MAPS,
  Msg,
  NO_PROP,
  PING_INTERVAL_MS,
  PITCH_LIMIT,
  PROP_CATALOGUE,
  TICK_MS,
  Team,
  buildWorld,
  clamp,
  decodeServerMessage,
  dequantizePitch,
  dequantizeXZ,
  dequantizeY,
  dequantizeYaw,
  encodeInput,
  encodePing,
  encodeHello,
  encodeStart,
  initialRoundState,
  mapHash,
  phaseAtTick,
  propSolidIndex,
  quantizePitch,
  quantizeYaw,
  roundStateAtTick,
  seqNewer,
  wrapAngle,
  type GameMap,
  type InputFrame,
  type PropCatalogue,
  type RoundPhaseId,
  type RoundState,
  type SelfState,
  type SnapshotMsg,
  type TagMsg,
  type TauntMsg,
  type StaticWorld,
  type TeamId,
  type WireInputFrame,
} from "@prop-hunt/shared";
import { ClockSync } from "./clock.js";
import { EntityTimeline } from "./interpolation.js";
import {
  RECONNECT_MAX_ATTEMPTS,
  isRetryableClose,
  reconnectDelayMs,
  reconnectingDetail,
} from "./reconnect.js";
import { foundFromMsg, roundStateFromMsg, teamsFromMsg } from "./round-state.js";
import {
  NULL_TRANSPORT,
  WebSocketTransport,
  type NetworkConditions,
  type Transport,
} from "./transport.js";

/**
 * The level a session turned out to be on, and the collision world built from
 * it. Handed to `main.ts` once, when the Welcome names it.
 */
export interface ResolvedLevel {
  id: string;
  map: GameMap;
  /** The one and only client-side world: predicted against, drawn, suppressed. */
  world: StaticWorld;
}

export type ConnectionStatus =
  | "connecting"
  | "open"
  /** Socket lost, a retry is scheduled or in flight. See reconnect.ts. */
  | "reconnecting"
  | "rejected"
  | "closed";

/**
 * Clock samples are cheap and the first few matter a great deal — until the
 * offset is trustworthy, remote entities cannot be placed on the timeline at
 * all. Ping hard, then settle down.
 */
const FAST_PING_SAMPLES = 5;
const FAST_PING_INTERVAL_MS = 200;

export class Connection {
  readonly clock = new ClockSync();
  readonly timelines = new Map<number, EntityTimeline>();
  readonly peers = new Map<number, string>();
  /** Every connected player's team, including our own. Empty until the first
   * Msg.Round arrives — before that, nobody has been assigned one. */
  readonly teams = new Map<number, TeamId>();
  /**
   * Players a hunter has found. Replaced wholesale from every Msg.Round rather
   * than accumulated from Msg.Tag events — see `foundFromMsg`.
   */
  readonly found = new Set<number>();

  status: ConnectionStatus = "connecting";
  detail = "";
  localId = 0;
  round: RoundState = initialRoundState();

  /**
   * Who may start the round, or 0 before the first Msg.Round.
   *
   * Read off the roster message rather than accumulated, for the reason
   * `found` is: it is state the server owns, and a client that missed a change
   * must end up right anyway. The server checks it again on every `Msg.Start`,
   * so this only decides whether a button is worth showing.
   */
  hostId = 0;

  /**
   * Tick the lobby will start itself on, or 0 for no pending auto-start.
   *
   * Display only. See `RoundMsg.startsAtTick` — the round can begin before
   * this, the moment the host presses START.
   */
  startsAtTick = 0;

  /** Newest authoritative self-state, awaiting reconciliation. */
  latestSelf: SelfState | null = null;
  latestAck = 0;
  hasNewAuthoritative = false;

  /**
   * The prop this client is wearing, straight from the newest snapshot.
   *
   * Possession is **not predicted**, deliberately, and this is the whole of
   * the client's possession state as a result. Predicting it would mean
   * predicting a *hull* change, and a hull disagreement is the one kind that
   * does not converge: while the two sides hold different boxes, every
   * collision resolves differently and reconciliation replays the difference
   * forward rather than absorbing it. The cost of waiting is one round trip
   * before the disguise appears, on an action performed while standing still.
   * That is a good trade, and it is the opposite call from movement for a
   * reason worth keeping straight — movement is continuous and self-correcting,
   * this is discrete and sticky.
   */
  selfPropIndex = NO_PROP;
  /**
   * Set for one frame when `selfPropIndex` changes, so main.ts can rebuild the
   * predictor's hull and treat the change as a hard reconciliation barrier.
   * Replaying inputs recorded under the old hull through the new one would
   * produce exactly the divergence not predicting it was meant to avoid.
   */
  propChanged = false;

  /**
   * Newest `Msg.Tag`, for the HUD and the reveal to pick up. Never cleared
   * here — a consumer that cares about *newness* compares identity, and one
   * that only wants the last thing that happened reads it as-is.
   */
  lastTag: TagMsg | null = null;

  /**
   * Newest `Msg.Taunt`, latched exactly like `lastTag` and read the same way —
   * a consumer that cares whether it is *new* compares identity.
   *
   * The server has already decided this client was close enough to hear it (see
   * `tauntAudible`), so there is nothing to filter here. Everything left is
   * presentation.
   */
  lastTaunt: TauntMsg | null = null;

  snapshotsReceived = 0;
  lastSnapshotAt = 0;

  /**
   * Incremented every time a `Msg.Welcome` establishes a *new* identity — so
   * once on the first join, and once per successful reconnect.
   *
   * A reconnect is a new player id on a socket that knows nothing about the
   * old one, which makes everything predicted or accumulated under the old id
   * wrong rather than stale: the predictor is holding a position for a player
   * the server has just respawned. Everything this class owns is reset in
   * `startSession` below; this is how `main.ts` learns to drop the one piece
   * it owns. A counter rather than a flag because it does not have to be
   * consumed to stay correct — a reader compares it against the last value it
   * acted on, so nothing is lost if two sessions start between two frames.
   */
  sessionEpoch = 0;

  private transport: Transport = NULL_TRANSPORT;
  /**
   * The socket every handler checks itself against.
   *
   * A retired socket can still deliver: `onerror` and `onclose` both fire for
   * one failure, and the conditions simulator holds messages in `setTimeout`
   * callbacks that outlive the socket that received them. Comparing identity
   * makes every one of those a no-op instead of a message applied to the
   * session that replaced it.
   */
  private socket: WebSocketTransport | null = null;
  private readonly writer = new ByteWriter();
  private readonly recentInputs: WireInputFrame[] = [];
  private inputSeq = 0;
  /** Newest tick stamped on an outgoing frame. See `nextFrameTick`. */
  private frameTick = 0;
  private lastPingAt = 0;
  private lastTick = 0;
  private hasTick = false;

  /** Retries since the last successful Welcome. Reset by `startSession`. */
  private reconnectAttempt = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  /** Set by `close()`, so a socket we hung up on is never retried. */
  private closedByUs = false;
  /**
   * Bytes over sockets that have already been retired.
   *
   * The counters live on the transport, and a reconnect replaces it. Without
   * carrying the totals across, the bandwidth readout would see its inputs
   * jump back to zero and report a negative rate for a window.
   */
  private bytesSentBefore = 0;
  private bytesReceivedBefore = 0;

  /**
   * The level this session is on, or `null` before the first Welcome names it.
   *
   * The client cannot know this in advance: a lobby is on a level chosen when it
   * was created, so a joiner is *told*. That is why the collision world is built
   * here rather than handed in — this class is where the answer arrives, and
   * there must be exactly one `StaticWorld` on the client, since possession
   * suppresses solids in it (see `applyPossessedSet`) and the renderer and the
   * predictor both have to be looking at the object being mutated.
   */
  private level: ResolvedLevel | null = null;

  constructor(
    private readonly url: string,
    private readonly playerName: string,
    private readonly conditions: NetworkConditions,
    private readonly catalogue: PropCatalogue = PROP_CATALOGUE,
    /**
     * Called once, with the level this session turned out to be on, before the
     * first snapshot can arrive. `main.ts` builds the renderer from it — which is
     * why it is a callback and not a getter polled from the frame loop: the
     * renderer must exist before anything is drawn, and exactly one of them.
     */
    private readonly onLevel: (level: ResolvedLevel) => void = () => {},
  ) {}

  connect(): void {
    this.closedByUs = false;
    this.status = "connecting";
    this.detail = "";
    this.openSocket();
  }

  close(): void {
    this.closedByUs = true;
    this.cancelReconnect();
    this.retireSocket();
    this.status = "closed";
  }

  private openSocket(): void {
    const ws: WebSocketTransport = new WebSocketTransport(
      this.url,
      {
        onOpen: () => {
          if (this.socket !== ws) return;
          // Nothing is resumed here. The server has no notion of this client
          // having been here before — identity is per-socket (see lobby.ts) —
          // so a reconnect is a fresh Hello and a fresh player id, and the
          // session state that went with the old one is dropped in
          // `startSession` when the Welcome comes back.
          ws.send(encodeHello(this.writer, this.playerName));
        },
        onMessage: (data) => {
          if (this.socket !== ws) return;
          this.onMessage(data);
        },
        onClose: (code, reason) => {
          if (this.socket !== ws) return;
          this.onDisconnected(reason || `connection closed (${code})`, code);
        },
        onError: () => {
          if (this.socket !== ws) return;
          // No code to judge, so treat it as retryable: `onclose` follows an
          // `onerror` on every real socket, and it carries one.
          this.onDisconnected("connection error", null);
        },
      },
      this.conditions,
    );
    this.socket = ws;
    this.transport = ws;
    ws.connect();
  }

  /**
   * Decide what a lost socket means, and either schedule a retry or stop.
   *
   * A rejection is terminal whatever the close code says. The Reject frame
   * arrives before the close on the same ordered socket, so by the time this
   * runs the status already carries the server's own explanation — and
   * reconnecting to a server that just refused this build is a way of failing
   * six times more slowly.
   */
  private onDisconnected(detail: string, code: number | null): void {
    this.retireSocket();

    if (this.status === "rejected") return;

    const retryable = code === null || isRetryableClose(code);
    if (!retryable || this.reconnectAttempt >= RECONNECT_MAX_ATTEMPTS) {
      this.status = "closed";
      this.detail = detail;
      return;
    }

    this.reconnectAttempt += 1;
    this.status = "reconnecting";
    this.detail = reconnectingDetail(this.reconnectAttempt);
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (this.closedByUs) return;
      this.openSocket();
    }, reconnectDelayMs(this.reconnectAttempt));
  }

  /** Detach the current socket, keeping its byte counters. Idempotent. */
  private retireSocket(code?: number, reason?: string): void {
    const ws = this.socket;
    if (ws === null) return;
    // Detached *before* the close, so that a close which reports itself
    // synchronously finds a socket the handlers no longer recognise and stops
    // there. Browsers deliver `onclose` on a later task and would not recurse;
    // relying on that would make this correct only by scheduling luck.
    this.socket = null;
    this.transport = NULL_TRANSPORT;
    this.bytesSentBefore += ws.bytesSent;
    this.bytesReceivedBefore += ws.bytesReceived;
    ws.close(code, reason);
  }

  private cancelReconnect(): void {
    if (this.reconnectTimer === null) return;
    clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
  }

  /**
   * Adopt a new identity and drop everything that belonged to the old one.
   *
   * Called for every Welcome, including the first, where it is a no-op over
   * freshly constructed state — one path rather than two is worth more here
   * than the handful of assignments it costs.
   *
   * The rule for what belongs in here: anything keyed by, or predicted from, a
   * player id the server no longer knows about. Cumulative *statistics* are
   * deliberately not reset — they describe this client's whole time on the
   * page, and zeroing them would make the debug overlay lie about a session
   * that did happen.
   */
  private startSession(yourId: number): void {
    this.timelines.clear();
    this.peers.clear();
    this.teams.clear();
    this.found.clear();
    this.clock.reset();

    this.localId = yourId;
    this.round = initialRoundState();
    // A reconnect is a new player in a lobby that has carried on without us:
    // the host may be somebody else by now, and a stale one would leave this
    // client showing a START it is not allowed to press.
    this.hostId = 0;
    this.startsAtTick = 0;
    this.latestSelf = null;
    this.latestAck = 0;
    this.hasNewAuthoritative = false;
    this.selfPropIndex = NO_PROP;
    this.propChanged = false;
    this.lastTag = null;
    this.lastTaunt = null;

    this.recentInputs.length = 0;
    this.inputSeq = 0;
    // Not carried across: it is an estimate of the *server's* tick, and the
    // lobby we have just joined may be a revived object counting from zero.
    // Held over, `nextFrameTick`'s strictly-increasing rule would then walk it
    // one tick per frame ahead of a server that clamps it back every time.
    this.frameTick = 0;
    this.lastPingAt = 0;
    this.lastTick = 0;
    this.hasTick = false;

    // Suppression is the one piece of session state that lives outside this
    // class, in the collision world main.ts also predicts against. Clear it:
    // the props worn a moment ago are not this session's business, and the
    // server sends the authoritative set right behind the Welcome.
    this.applyPossessedSet([]);

    this.reconnectAttempt = 0;
    this.status = "open";
    this.detail = "";
    this.sessionEpoch += 1;
  }

  /**
   * Which level this session is on, or null before the Welcome named it.
   *
   * The registry id, which is also the map's name — `maps/index.test.ts` holds
   * those equal so there is no second string to drift.
   */
  get levelId(): string | null {
    return this.level?.id ?? null;
  }

  /** Newest server tick seen, low 16 bits. Wraps; display only. */
  get serverTick(): number {
    return this.lastTick;
  }

  /** This client's own team, or Unassigned before the first Round message. */
  get myTeam(): TeamId {
    return this.teams.get(this.localId) ?? Team.Unassigned;
  }

  /** Whether this client has been found, and is therefore spectating. */
  get amFound(): boolean {
    return this.found.has(this.localId);
  }

  /**
   * Whether this client is the one whose START begins the round.
   *
   * `hostId` of 0 is an empty answer rather than a match, which matters
   * because `localId` is also 0 before the Welcome lands.
   */
  get amHost(): boolean {
    return this.hostId !== 0 && this.hostId === this.localId;
  }

  /**
   * Ask the server to start the round.
   *
   * Sends and hopes: the server re-checks that this client is the host, that
   * the lobby is in Warmup and that it has the players for a round, and drops
   * the message otherwise. Nothing here predicts the transition — the round
   * begins when a `Msg.Round` says it has, exactly as it does for every other
   * player in the lobby.
   */
  sendStart(): void {
    this.transport.send(encodeStart(this.writer));
  }

  /** Monotonic across reconnects; see `bytesSentBefore`. */
  get bytesSent(): number {
    return this.bytesSentBefore + this.transport.bytesSent;
  }

  get bytesReceived(): number {
    return this.bytesReceivedBefore + this.transport.bytesReceived;
  }

  /**
   * Stamp an input frame, ship it, and hand back the frame the predictor
   * should simulate.
   *
   * The returned frame carries the *dequantized* angles rather than the raw
   * mouse-derived ones. Predicting with full precision while the server
   * replays the rounded value it received would put a small guaranteed
   * disagreement into every single tick — a permanent low-level correction
   * that looks like jitter and has no obvious cause.
   */
  sendInput(buttons: number, rawYaw: number, rawPitch: number, now: number): InputFrame {
    this.inputSeq = (this.inputSeq + 1) & 0xffff;

    const yawQ = quantizeYaw(wrapAngle(rawYaw));
    const pitchQ = quantizePitch(clamp(rawPitch, -PITCH_LIMIT, PITCH_LIMIT));

    this.recentInputs.push({
      seq: this.inputSeq,
      buttons,
      yawQ,
      pitchQ,
      tick: this.nextFrameTick(now),
    });
    while (this.recentInputs.length > INPUT_REDUNDANCY) this.recentInputs.shift();

    // Every packet repeats the last few frames. Costs 5 bytes each and means a
    // delayed packet does not leave a hole in the server's input stream.
    this.transport.send(encodeInput(this.writer, this.recentInputs));

    return {
      seq: this.inputSeq,
      buttons,
      yaw: dequantizeYaw(yawQ),
      pitch: dequantizePitch(pitchQ),
    };
  }

  /**
   * The tick to stamp on the input frame being sent right now.
   *
   * Anchored to the clock rather than counted from zero, because the server
   * reads it against its own tick — see `resolveFrameTick`. It is the same
   * estimate `gatePhase` derives the phase from, which is the point: the frame
   * says which tick it was gated at, so the server can gate it identically
   * instead of using whichever tick it happened to dequeue it on.
   *
   * Forced strictly increasing. Two fixed steps can run in one animation frame
   * (the accumulator carried two ticks, i.e. the frame rate dipped below 20fps)
   * and `now` does not advance between them, so the estimate would repeat and
   * two frames would claim one tick. Advancing by one instead may claim a tick
   * slightly ahead of the server, which the server clamps back — a bounded,
   * self-correcting overshoot, where a repeat would silently recreate the
   * batched-gate problem this field exists to fix.
   *
   * Before the first pong there is no estimate and this counts from zero, which
   * the server clamps to its own tick minus MAX_INPUT_TICK_LAG. That window is
   * Warmup or a mid-round join, where `canMove` does not distinguish teams and
   * a shot cannot be fired, so nothing reads a tick it would get wrong.
   */
  private nextFrameTick(now: number): number {
    const estimate = this.estimatedServerTick(now);
    const anchored = estimate === null ? 0 : Math.round(estimate);
    this.frameTick = anchored > this.frameTick ? anchored : this.frameTick + 1;
    return this.frameTick;
  }

  maybePing(now: number): void {
    const interval =
      this.clock.samples < FAST_PING_SAMPLES ? FAST_PING_INTERVAL_MS : PING_INTERVAL_MS;
    if (now - this.lastPingAt < interval) return;
    this.lastPingAt = now;
    this.transport.send(encodePing(this.writer, now));
  }

  /** Server simulation time that remote entities should be rendered at. */
  renderTime(now: number, interpDelayMs: number): number {
    return this.clock.serverNow(now) - interpDelayMs;
  }

  /**
   * Unwrapped estimate of the tick the server is on right now, or `null`
   * before the first pong makes the clock offset mean anything.
   *
   * `serverTimeMs` is `tick * TICK_MS` by construction on the server — never a
   * wall clock, for the reason lobby.ts gives — so dividing the synced server
   * time back by TICK_MS recovers its tick.
   *
   * This is the counterpart to `serverTick` and not a substitute for it: that
   * one is the newest *received* snapshot's low 16 bits, which wraps and lags
   * by however long the last packet took. This one is unwrapped and current,
   * which is what a u32 `phaseStartedTick` has to be subtracted from.
   */
  estimatedServerTick(now: number): number | null {
    return this.clock.ready ? this.clock.serverNow(now) / TICK_MS : null;
  }

  /**
   * The phase to gate movement on, for the tick being predicted right now.
   *
   * Not `this.round.phase`: that is the phase the newest Msg.Round carried,
   * which the server left ~RTT/2 ago. See `phaseAtTick` in shared/round.ts for
   * what that costs and why deriving it fixes the gate rather than papering
   * over it.
   *
   * Before the clock is ready, fall back to the phase we were last told — the
   * pre-existing behaviour, and no worse than it.
   */
  gatePhase(now: number): RoundPhaseId {
    const tick = this.estimatedServerTick(now);
    return tick === null ? this.round.phase : phaseAtTick(this.round, tick);
  }

  /**
   * The round as of now, for display. Same derivation as `gatePhase`, so the
   * HUD's banner cannot contradict what the movement gate is actually doing.
   */
  roundNow(now: number): RoundState {
    const tick = this.estimatedServerTick(now);
    return tick === null ? this.round : roundStateAtTick(this.round, tick);
  }

  private onMessage(data: ArrayBuffer): void {
    const msg = decodeServerMessage(data);
    if (msg === null) return;
    const now = performance.now();

    switch (msg.type) {
      case Msg.Welcome:
        // Before anything else: which level is this, and do we have it? Nothing
        // below can be believed until the answer is yes — `startSession` clears
        // suppression against the collision world, and every snapshot after it
        // describes positions in that geometry.
        if (!this.resolveLevel(msg.mapId, msg.mapHash)) break;
        // Clears the timelines wholesale, which subsumes what this case used
        // to do by hand: anything buffered under our own id is a timeline for
        // ourselves, built from snapshots that arrived before we knew which
        // entity we were. It can never be fed again — `onSnapshot` skips the
        // local entity — so left in place it starves forever, and shows up as
        // one phantom entity and 100% interpolation starvation for the whole
        // session. See the note in reapVanishedEntities.
        this.startSession(msg.yourId);
        for (const peer of msg.peers) this.peers.set(peer.id, peer.name);
        // Deliberately NOT seeding the clock from this message: it would imply
        // a zero round trip and bias the offset by the one-way latency, and
        // the EMA would then take ten seconds to walk that off. An immediate
        // ping gives an honest first sample instead.
        this.maybePing(now);
        break;

      case Msg.PeerJoin:
        this.peers.set(msg.id, msg.name);
        break;

      case Msg.PeerLeave:
        this.peers.delete(msg.id);
        this.timelines.delete(msg.id);
        break;

      case Msg.Pong:
        this.clock.onPong(msg.clientTime, msg.serverTimeMs, now);
        break;

      case Msg.Reject:
        this.status = "rejected";
        this.detail = msg.detail;
        break;

      case Msg.Snapshot:
        this.onSnapshot(msg, now);
        break;

      case Msg.Round:
        this.round = roundStateFromMsg(msg);
        this.hostId = msg.hostId;
        this.startsAtTick = msg.startsAtTick;
        this.teams.clear();
        for (const [id, team] of teamsFromMsg(msg)) this.teams.set(id, team);
        this.found.clear();
        for (const id of foundFromMsg(msg)) this.found.add(id);
        break;

      case Msg.Tag:
        // Latched rather than acted on here: this is an event for the renderer
        // and the HUD to consume on the next frame, and nothing about who is
        // still playing is decided by it. That is Msg.Round's job.
        this.lastTag = msg;
        break;

      case Msg.Taunt:
        // Same treatment as Msg.Tag, and for a stronger reason: a taunt is
        // sound and a HUD line and nothing else. No state anywhere is derived
        // from it, so dropping one costs exactly one noise nobody heard.
        this.lastTaunt = msg;
        break;

      case Msg.Possess:
        this.applyPossessedSet(msg.propIndices);
        break;
    }
  }

  private onSnapshot(msg: SnapshotMsg, now: number): void {
    // WebSocket is ordered, so an out-of-order snapshot should be impossible.
    // Checking costs one comparison and removes a whole category of "why did
    // that entity rewind" investigation.
    if (this.hasTick && !seqNewer(msg.tick, this.lastTick)) return;
    this.lastTick = msg.tick;
    this.hasTick = true;

    this.snapshotsReceived += 1;
    this.lastSnapshotAt = now;

    if (msg.selfPropIndex !== this.selfPropIndex) {
      this.selfPropIndex = msg.selfPropIndex;
      // Latched rather than assigned, so a possession that lands between two
      // frames is not lost by a second snapshot arriving first.
      this.propChanged = true;
    }

    this.latestSelf = msg.self;
    this.latestAck = msg.lastProcessedSeq;
    this.hasNewAuthoritative = true;

    for (const entity of msg.entities) {
      // The local player is predicted, never interpolated. Rendering yourself
      // 100ms in the past to be consistent with everyone else would make your
      // own controls feel broken for no benefit.
      if (entity.id === this.localId) continue;

      let timeline = this.timelines.get(entity.id);
      if (timeline === undefined) {
        // Get-or-create, never get-or-drop. A snapshot can legitimately
        // mention a player whose PeerJoin has not been handled yet, and
        // discarding it silently loses a tick of their motion.
        timeline = new EntityTimeline();
        this.timelines.set(entity.id, timeline);
      }

      timeline.push({
        time: msg.serverTimeMs,
        x: dequantizeXZ(entity.xQ),
        y: dequantizeY(entity.yQ),
        z: dequantizeXZ(entity.zQ),
        yaw: dequantizeYaw(entity.yawQ),
        propId: entity.propId,
        flags: entity.flags,
      });
    }

    this.reapVanishedEntities(msg);
  }

  /**
   * Mirror the server's suppressed solids onto our collision world.
   *
   * A possessed prop's box has to leave the world on both sides or the two
   * simulate different levels, and unlike a positional disagreement that one
   * never converges — the client would keep colliding with a crate the server
   * says is not there, every tick, forever.
   *
   * Applied as a whole set rather than a delta (see `Msg.Possess`): everything
   * named is suppressed, everything else is restored. That makes the operation
   * idempotent and self-repairing, so a client that somehow missed a message
   * is corrected by the next one instead of staying one prop out of step.
   */
  private applyPossessedSet(propIndices: readonly number[]): void {
    // No level yet means no world to suppress anything in. Unreachable in
    // practice — Possess only follows a Welcome, and a Welcome that failed to
    // resolve a level closes the socket — but suppression against a world this
    // client never built is not a thing to leave expressible.
    const level = this.level;
    if (level === null) return;
    const wanted = new Set(propIndices);
    for (let i = 0; i < level.map.props.length; i++) {
      const solid = propSolidIndex(level.map, this.catalogue, i);
      if (solid >= 0) level.world.setSuppressed(solid, wanted.has(i));
    }
  }

  /**
   * Settle which level this session runs on, from the server's Welcome.
   *
   * Returns false when this client cannot honour it, having already closed the
   * socket — the caller must not go on to start a session.
   *
   * This is where map agreement lives now. It used to be a hash in `Hello` that
   * the server checked, which stopped being answerable once a lobby could be on
   * a level of its own: a joiner has not been told which map it is on by the
   * time it introduces itself. Checking here is strictly better informed — the
   * server names the level as well as hashing it — and it is the client that is
   * wrong in the case that matters, so it is the client that can say so.
   */
  private resolveLevel(id: string, serverHash: number): boolean {
    const existing = this.level;
    if (existing !== null) {
      // A reconnect. A lobby's level is fixed for its life, so this must be the
      // same one, and the `StaticWorld` must stay the very same object: the
      // renderer and the predictor hold references to it, and swapping it would
      // leave them simulating and drawing different geometry.
      if (existing.id === id) return true;
      this.rejectLevel(`lobby changed level from "${existing.id}" to "${id}"; reload the page`);
      return false;
    }

    const map = MAPS.get(id);
    if (map === undefined) {
      // A server offering a level this build has never heard of. Almost always a
      // deploy this tab predates.
      this.rejectLevel(`this build has no level called "${id}"; reload the page`);
      return false;
    }

    const ourHash = mapHash(map, this.catalogue);
    if (ourHash !== serverHash) {
      this.rejectLevel(
        `level "${id}" here is not the server's ` +
          `(${hex32(serverHash)}, we built ${hex32(ourHash)}); reload the page`,
      );
      return false;
    }

    const level: ResolvedLevel = { id, map, world: buildWorld(map, this.catalogue) };
    this.level = level;
    this.onLevel(level);
    return true;
  }

  /**
   * Stop, terminally, because we cannot agree with the server about geometry.
   *
   * Terminal rather than retryable: reconnecting would ask the same question and
   * get the same answer, six times more slowly. `status = "rejected"` is what
   * `onDisconnected` reads to know not to retry, and the close carries
   * `CloseCode.MapMismatch` so the reason is legible in the server's log too and
   * not only in this tab.
   */
  private rejectLevel(detail: string): void {
    this.status = "rejected";
    this.detail = detail;
    this.closedByUs = true;
    this.cancelReconnect();
    this.retireSocket(CloseCode.MapMismatch, "map mismatch");
  }

  /**
   * Drop timelines for entities the server has stopped sending.
   *
   * PeerLeave normally handles this, but a snapshot is the authoritative
   * statement of who exists — relying on the notification alone leaves ghosts
   * standing around whenever the two disagree.
   *
   * The local player is the one entity this cannot reason about by presence.
   * The server includes us in our own snapshot, so a timeline under our own id
   * always looks present and is never reaped — while `applySnapshot` skips the
   * local entity and never feeds it. That combination turns a single
   * mis-timed snapshot into a permanently starving phantom, so the local id is
   * dropped explicitly rather than tested for presence.
   */
  private reapVanishedEntities(msg: SnapshotMsg): void {
    if (this.timelines.size === 0) return;
    this.timelines.delete(this.localId);
    for (const id of [...this.timelines.keys()]) {
      let present = false;
      for (const entity of msg.entities) {
        if (entity.id === id) {
          present = true;
          break;
        }
      }
      if (!present) this.timelines.delete(id);
    }
  }
}

/** Map hashes read as hex everywhere they are shown to a person. See mapHashHex. */
function hex32(v: number): string {
  return (v >>> 0).toString(16).padStart(8, "0");
}
